import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import PolicyholderFnol from "@icg360/fnol-policyholder";

import { AuthAppContext } from "components/root/auth-app-provider";
import { FullpageSpinner } from "components/shared/fullpage-spinner";
import { CONFIG } from "config";
import { useClaimsConstants } from "data/ss-data-hooks";
import { useKeystone } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import {
  type PolicyHolderDetails,
  useExternalClaimSubmitQuery,
} from "gql/__generated__/hooks";
import { PageError } from "pages/page-error";
import { trackEvent, trackFNOLEvent } from "utils";

const ExternalClaimSubmit = () => {
  const keystone = useKeystone();
  const navigate = useNavigate();
  const { selectedPolicyId, userInfo } = useContext(AuthAppContext);
  const [pageError, setPageError] = useState<boolean>(false);
  const { isLoading: isConstantsLoading, data: claimsConstants } =
    useClaimsConstants();
  const [policyHolderDetails, setPolicyHolderDetails] =
    useState<PolicyHolderDetails>();

  useEffect(() => {
    trackEvent("Claim Submit Form: Displayed");
  }, []);

  const {
    loading,
    error: gqlError,
    data: policyDetails,
  } = useExternalClaimSubmitQuery({
    variables: {
      policyID: selectedPolicyId,
    },
  });

  useEffect(() => {
    if (gqlError) setPageError(true);

    if (!isConstantsLoading && claimsConstants) {
      setPolicyHolderDetails(claimsConstants.PolicyHolderDetails);
    }
  }, [gqlError, claimsConstants?.PolicyHolderDetails, isConstantsLoading]);

  if (loading) {
    return <FullpageSpinner />;
  }

  if (pageError) {
    return <PageError />;
  }

  const { userDetails, userPolicyProperties } = policyDetails ?? {};

  const { propertyAddress, currentTerm } = userDetails ?? {};

  const policy = {
    policyId: selectedPolicyId,
    policyName: `${userPolicyProperties?.carrierName} (${userPolicyProperties?.policyType})`,
    policyEffectiveDate: currentTerm?.effectiveDate,
    policyExpirationDate: currentTerm?.expirationDate,
    fullName: policyHolderDetails?.name ?? "",
    email: policyHolderDetails?.emailAddress ?? "",
    phoneNumber: policyHolderDetails?.phoneNumber ?? "",
    streetAddress: `${propertyAddress?.street1} ${propertyAddress?.street2}`,
    city: propertyAddress?.city ?? "",
    state: propertyAddress?.state ?? "",
    zip: propertyAddress?.zip ?? "",
    carrierGroup: userPolicyProperties?.carrierGroup ?? "",
    policyType: userPolicyProperties?.policyType ?? "",
  };

  const handleFNOLPostApi = async (event) => {
    const response = await keystone.fnolChannelSubmit(event);
    if (isError(response)) {
      setPageError(true);
    }
    return response;
  };

  const handleFormExit = () => {
    navigate("/my/claims");
  };

  return (
    <PolicyholderFnol
      definitionLocation={CONFIG.FNOL_DEFINITIONS_URL}
      policy={policy}
      user={userInfo?.email ?? undefined}
      googleMapsKey={CONFIG.GOOGLE_MAPS_KEY}
      onPost={handleFNOLPostApi}
      onExit={handleFormExit}
      onEvent={(event) => {
        trackFNOLEvent(event);
      }}
    />
  );
};

export default ExternalClaimSubmit;
